import PropTypes from 'prop-types';
import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import Icon from '../Icon';
import PlainButton from '../PlainButton';
import { tokens, orange, inkA } from '../tokens';
import useIsMobile from '../useIsMobile';

// Unsaved-changes bar, sticky to the bottom of the page while there is
// something to save: "N unsaved changes", a row of change pills, Discard
// and Save changes. On phones it spans the width and, as in the design
// (builder-mobile.css), drops the pills: a stacked "Unsaved / N" count,
// one divider, then the two buttons. Below NARROW px the save button says
// `shortSaveLabel` so both buttons fit on a 360px screen, and the buttons
// may shrink (ellipsis) rather than push the page sideways.
//
// pills: [{ key, label, count?, static? }]. Every pill is a button that
// calls onPill(pill), except `static: true` ones (e.g. "+3 more") and all
// of them when onPill is absent. `saving` disables both buttons and shows
// "Saving…". `hint` is a one-line note inside the bar, under the buttons.

// Below this width "Save changes" no longer fits beside Discard.
const NARROW = 390;

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const clip = { overflow: 'hidden', textOverflow: 'ellipsis' };

const pillCss = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  max-width: 200px;
  padding: 5px 13px 5px ${(p) => (p.$hasCount ? '5px' : '13px')};
  border-radius: 999px;
  border: 1px solid ${orange(0.35)};
  background: ${orange(0.08)};
  color: ${(p) => p.$text};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const Pill = styled(PlainButton)`
  ${pillCss}
  transition: background 150ms ease, border-color 150ms ease;

  &:hover {
    background: ${orange(0.16)};
    border-color: ${orange(0.65)};
  }
`;

const StaticPill = styled.span`
  ${pillCss}
  background: transparent;
  border-color: ${(p) => p.$line};
`;

const BarButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  height: ${(p) => (p.$compact ? '42px' : '44px')};
  padding: 0 ${(p) => (p.$compact ? '14px' : '18px')};
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  box-shadow: ${(p) => p.$shadow || 'none'};

  &:disabled {
    opacity: 0.6;
  }
`;

const PillCount = ({ n }) => (
  <span
    style={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      background: orange(1),
      color: '#fff',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 800,
      flexShrink: 0,
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    {n}
  </span>
);

PillCount.propTypes = { n: PropTypes.number.isRequired };

const Divider = ({ color }) => (
  <span
    aria-hidden="true"
    style={{
      width: 1,
      alignSelf: 'stretch',
      background: color,
      flexShrink: 0,
    }}
  />
);

Divider.propTypes = { color: PropTypes.string.isRequired };

const changesText = (count) => `unsaved change${count === 1 ? '' : 's'}`;

// Desktop: "3 unsaved changes" on one line. Phone: "UNSAVED" over the
// number, as in the design; screen readers still hear "3 unsaved changes".
const Count = ({ count, stacked, t }) => {
  if (stacked) {
    return (
      <div
        aria-live="polite"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexShrink: 0,
          padding: '2px 6px',
        }}
      >
        <span style={VISUALLY_HIDDEN}>{`${count} ${changesText(count)}`}</span>
        <span
          aria-hidden="true"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: t.sub,
            lineHeight: 1,
          }}
        >
          Unsaved
        </span>
        <span
          aria-hidden="true"
          style={{
            marginTop: 5,
            fontSize: 22,
            fontWeight: 800,
            color: t.primary,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      </div>
    );
  }
  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        flexShrink: 0,
        padding: '0 4px',
      }}
    >
      <span
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: t.primary,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count}
      </span>
      <span
        style={{ fontSize: 12, fontWeight: 700, color: t.sub, lineHeight: 1.2 }}
      >
        {changesText(count)}
      </span>
    </div>
  );
};

Count.propTypes = {
  count: PropTypes.number.isRequired,
  stacked: PropTypes.bool.isRequired,
  t: PropTypes.object.isRequired,
};

const Pills = ({ pills, onPill, t }) => (
  <div
    style={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      overflowX: 'auto',
      padding: '2px 0',
    }}
  >
    {pills.map((pill) => {
      const hasCount = typeof pill.count === 'number';
      const content = (
        <>
          {hasCount ? <PillCount n={pill.count} /> : null}
          <span style={clip}>{pill.label}</span>
        </>
      );
      if (!onPill || pill.static) {
        return (
          <StaticPill
            key={pill.key}
            $hasCount={hasCount}
            $text={t.sub}
            $line={t.border}
          >
            {content}
          </StaticPill>
        );
      }
      return (
        <Pill
          key={pill.key}
          onClick={() => onPill(pill)}
          title={`Jump to ${pill.label}`}
          $hasCount={hasCount}
          $text={t.text}
        >
          {content}
        </Pill>
      );
    })}
  </div>
);

Pills.propTypes = {
  pills: PropTypes.arrayOf(PropTypes.object).isRequired,
  onPill: PropTypes.func,
  t: PropTypes.object.isRequired,
};

Pills.defaultProps = { onPill: undefined };

// Phone vs desktop sizes (design: builder.css .ubar, builder-mobile.css).
const LAYOUT = {
  phone: {
    regionPad: '8px 0 calc(8px + env(safe-area-inset-bottom, 0px))',
    maxWidth: 'none',
    cardPad: '8px 10px',
    radius: 14,
    gap: 10,
    hintGap: 8,
    hintPad: 7,
  },
  desktop: {
    regionPad: '12px 0 16px',
    maxWidth: 880,
    cardPad: '10px 14px',
    radius: 16,
    gap: 14,
    hintGap: 10,
    hintPad: 8,
  },
};

// Discard + Save. On phones the group may shrink (its labels ellipsize)
// and sits at the right edge after the divider.
const Actions = ({ compact, saving, label, onDiscard, onSave, theme, t }) => (
  <div
    style={{
      display: 'flex',
      gap: 8,
      marginLeft: compact ? 'auto' : 0,
      minWidth: 0,
      flexShrink: compact ? 1 : 0,
    }}
  >
    <BarButton
      onClick={onDiscard}
      disabled={saving}
      $compact={compact}
      $bg={inkA(theme, 0.06)}
      $color={t.sub}
    >
      <span style={clip}>Discard</span>
    </BarButton>
    <BarButton
      onClick={onSave}
      disabled={saving}
      $compact={compact}
      $bg={t.primary}
      $color="#fff"
      $shadow={`0 6px 18px ${orange(0.3)}`}
    >
      <Icon name="save" size={18} color="currentColor" />
      <span style={clip}>{saving ? 'Saving…' : label}</span>
    </BarButton>
  </div>
);

Actions.propTypes = {
  compact: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  theme: PropTypes.object,
  t: PropTypes.object.isRequired,
};

Actions.defaultProps = { theme: undefined };

const SaveBar = ({
  count,
  pills,
  onPill,
  onDiscard,
  onSave,
  saving,
  hint,
  saveLabel,
  shortSaveLabel,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const isMobile = useIsMobile();
  const isNarrow = useIsMobile(NARROW);
  const size = LAYOUT[isMobile ? 'phone' : 'desktop'];

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 50,
        padding: size.regionPad,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: size.maxWidth,
          margin: '0 auto',
          boxSizing: 'border-box',
          padding: size.cardPad,
          borderRadius: size.radius,
          background: t.card,
          border: '1px solid ' + t.border,
          boxShadow: t.shadowPop,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: size.gap,
            minWidth: 0,
          }}
        >
          <Count count={count} stacked={isMobile} t={t} />
          {/* Phones drop the pills (and their divider). On desktop the
              pill row, even when empty, is the spacer before the buttons. */}
          {!isMobile && pills.length ? <Divider color={t.border} /> : null}
          {isMobile ? null : <Pills pills={pills} onPill={onPill} t={t} />}
          <Divider color={t.border} />
          <Actions
            compact={isMobile}
            saving={saving}
            label={isMobile && isNarrow ? shortSaveLabel : saveLabel}
            onDiscard={onDiscard}
            onSave={onSave}
            theme={theme}
            t={t}
          />
        </div>
        {hint ? (
          <div
            style={{
              marginTop: size.hintGap,
              paddingTop: size.hintPad,
              borderTop: '1px solid ' + t.line,
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
              color: t.sub,
            }}
          >
            {hint}
          </div>
        ) : null}
      </div>
    </div>
  );
};

SaveBar.propTypes = {
  count: PropTypes.number.isRequired,
  pills: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
      static: PropTypes.bool,
    })
  ),
  onPill: PropTypes.func,
  onDiscard: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  hint: PropTypes.node,
  saveLabel: PropTypes.string,
  // The save label on phones narrower than NARROW px.
  shortSaveLabel: PropTypes.string,
};

SaveBar.defaultProps = {
  pills: [],
  onPill: undefined,
  saving: false,
  hint: null,
  saveLabel: 'Save changes',
  shortSaveLabel: 'Save',
};

export default SaveBar;
